import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
    CBadge,
    CFormGroup,
    CLabel,
    CInput,
    CTextarea,
    CButton,
    CButtonGroup,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CSpinner,
    CRow,
    CCol,
    CSelect,
    CSwitch,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { getApi } from "../../apis/backendApis";
import Form, { FormApi } from "../../components/form";
import useCustomForm from '../../services/useCustomForm';
import useCurrentAction from "../../services/useCurrentAction";
import { NotificacaodestinatarioList, NotificacaoTipoList, NotificacaoDestinatario } from "../notificacao";
import { listRegimeTributacaoFiltro } from "../clients";
import '../../scss/styles-secrets-manager.scss';

// Espelha SecretsManager::TIPO_* e SecretsManager::PARTE_TIPO_*
const TIPO_SIMPLES  = 1;
const TIPO_COMPOSTA = 2;
const PARTE_TEXTO = 1;
const PARTE_SENHA = 2;
const PARTE_URL   = 3;

const TIPOS_PARTE: Record<number, string> = { [PARTE_TEXTO]: 'Texto', [PARTE_SENHA]: 'Senha', [PARTE_URL]: 'URL' };

type Parte = { campo: string; valor: string; tipo: number };
type ModoRevelar = 'ver' | 'editar';

const partePadrao = (): Parte => ({ campo: '', valor: '', tipo: PARTE_TEXTO });

// Espelha SecretsManagerMensagem::VARIAVEIS: substituídas no envio
const VARIAVEIS_MENSAGEM = ['Identificador', 'DataExpiracao', 'DiasRestantes', 'Responsavel', 'Cliente'];
const PADRAO_VARIAVEL = /\{\{\s*([^{}]*?)\s*\}\}/g;

const variaveisDesconhecidas = (mensagem: string): string[] => {
    const nomes = Array.from(mensagem.matchAll(PADRAO_VARIAVEL)).map(m => m[1]);
    return nomes.filter((nome, i) => !VARIAVEIS_MENSAGEM.includes(nome) && nomes.indexOf(nome) === i);
};

const formatarDataHora = (valor: string) => {
    const data = valor ? new Date(valor) : null;
    if (!data || isNaN(data.getTime())) return '';
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${data.getFullYear()} ${dois(data.getHours())}:${dois(data.getMinutes())}`;
};

// Dias entre hoje e o vencimento, contados por data (negativo depois do vencimento), como no backend
const diasAteVencimento = (valor: string) => {
    const data = new Date(valor);
    const hoje = new Date();
    const utc = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    return Math.round((utc(data) - utc(hoje)) / 86400000);
};

type Opcao = { value: any; label: string };
type Grupo = { setor: Opcao; visualizar: boolean; revelar: boolean; editar: boolean };
type Permissoes = { visualizar: boolean; revelar: boolean; editar: boolean };
type Envio = { id: number; dia: string; geradoEm: string; enviadoEm: string | null; destino: string; resultado: string; detalhe: string; vencimento: string };
type Alteracao = { id: number; dataHora: string; usuario: string; acao: string; detalhe: string | null };

// Espelha SecretsManagerHistorico::ACAO_*
const CORES_ACAO: Record<string, string> = {
    'Inclusão': 'success',
    'Alteração': 'info',
    'Exclusão': 'danger',
    'Segredo revelado': 'warning',
    'Senha inválida ao revelar': 'danger',
    'Recuperado pela API': 'secondary',
};

const CORES_RESULTADO: Record<string, string> = { 'Enviado': 'success', 'Erro': 'danger', 'Pendente': 'warning' };

// Chaves da resposta do GET que não voltam no envio do formulário
const CHAVES_SO_LEITURA = ['permissoes', 'historicoNotificacoes', 'historicoAlteracoes', 'setores', 'notificacaoTiposEnvio', 'clientesNotificacao',
    'notificar', 'notificacaoDiasAntes', 'marketplaces', 'gatewayPagamento', 'formaPagamento', 'regimeTributacao'];

const isListaDePartes = (valor: any): valor is Parte[] =>
    Array.isArray(valor) && valor.length > 0 && valor.every(p => p && typeof p === 'object' && 'campo' in p && 'valor' in p);

const SecretsManagerForm = () => {
    const [formApi, setFormApi] = useState<FormApi>();
    const [showModal, setShowModal] = useState(false);
    const [modoRevelar, setModoRevelar] = useState<ModoRevelar>('ver');
    const [passwordInput, setPasswordInput] = useState('');
    const [erroSenha, setErroSenha] = useState('');
    const [loading, setLoading] = useState(false);

    // Credencial gravada (cifrada) e valor que conta como "sem alteração" para o newCredencial
    const [firstValueCredencial, setFirstValueCredencial] = useState<any>();
    const [firstTipoCredencial, setFirstTipoCredencial] = useState<number>(TIPO_SIMPLES);
    const [credencialBase, setCredencialBase] = useState<any>();
    const [credencialAberta, setCredencialAberta] = useState(false);

    // Valores revelados na visualização
    const [decryptedValue, setDecryptedValue] = useState('');
    const [decryptedPartes, setDecryptedPartes] = useState<Parte[] | null>(null);

    const [partes, setPartes] = useState<Parte[]>([partePadrao()]);
    const [valoresVisiveis, setValoresVisiveis] = useState<Record<number, boolean>>({});
    const [copiado, setCopiado] = useState<string | null>(null);

    // Notificação
    const [notificarResponsavel, setNotificarResponsavel] = useState(false);
    const [tiposEnvioSelected, setTiposEnvioSelected] = useState<any[]>([]);
    const [webhookMattermost, setWebhookMattermost] = useState<any>(null);
    const [listWebhooks, setListWebhooks] = useState<any[]>([]);
    const [notificacaoMensagem, setNotificacaoMensagem] = useState('');
    const [diasAntesExpiracao, setDiasAntesExpiracao] = useState('');
    const [gateway, setGateway] = useState<any[]>([]);
    const [formaPagamento, setFormaPagamento] = useState<any[]>([]);
    const [marketplace, setMarketplace] = useState<any[]>([]);
    const [regimeTributacao, setRegimeTributacao] = useState<any[]>([]);
    const [clientes, setClientes] = useState<any[]>([]);
    const [listGateway, setListGateway] = useState<any[]>([]);
    const [listFormaPagamento, setListFormaPagamento] = useState<any[]>([]);
    const [listMarketplace, setListMarketplace] = useState<any[]>([]);
    // Filtros da Notificação Admin gravados, aplicados quando as listas chegam
    const [filtrosSalvos, setFiltrosSalvos] = useState<any>(null);
    const mensagemRef = useRef<HTMLTextAreaElement>(null);

    // Grupos de acesso, responsável e histórico
    const [opcoesSetores, setOpcoesSetores] = useState<(Opcao & { membros: number })[]>([]);
    const [opcoesUsuarios, setOpcoesUsuarios] = useState<Opcao[]>([]);
    const [grupos, setGrupos] = useState<Grupo[]>([]);
    // Nada liberado até o GET responder: num 403 a tela fica sem as ações (no cadastro novo não é usado)
    const [permissoes, setPermissoes] = useState<Permissoes>({ visualizar: false, revelar: false, editar: false });
    const [historico, setHistorico] = useState<Envio[]>([]);
    const [alteracoes, setAlteracoes] = useState<Alteracao[]>([]);
    const [abaHistorico, setAbaHistorico] = useState<'notificacoes' | 'alteracoes'>('notificacoes');
    const history = useHistory();

    const tiposEnvioOptions = [
        { value: 1, label: 'Mattermost' },
        { value: 2, label: 'Notificação Admin' },
    ];

    const hasTipoEnvio = (tipo: number) => tiposEnvioSelected.some((t: any) => t.value === tipo);

    const action = useCurrentAction();

    const initialValues = {
        cliente: null,
        identificador: '',
        credencial: '',
        dataExpiracao: '',
        tipoCredencial: TIPO_SIMPLES,
        destinatario: '',
        tipoNotificacao: '',
    };

    const {
        values,
        setValues,
        handleChange,
        handleSubmit,
        handleSubmitValues,
        handleChangeSuggest,
    } = useCustomForm({
        initialValues,
        route: 'secrets-manager',
        onSubmit: (data: any) => {
            const submitData = { ...data.values };
            CHAVES_SO_LEITURA.forEach(chave => delete submitData[chave]);
            const editandoPartes = !(submitData.id > 0) || credencialAberta;
            if (Number(submitData.tipoCredencial) === TIPO_COMPOSTA && editandoPartes
                && partes.some(p => !p.campo.trim() || !p.valor)) {
                alert('Preencha o campo e o valor de todas as partes da credencial.');
                return;
            }
            if (!grupos.some(g => g.visualizar)) {
                alert('Adicione ao menos um grupo de acesso com permissão de visualizar a credencial.');
                return;
            }
            submitData.setores = grupos.map(g => ({
                setor: g.setor.value,
                visualizar: g.visualizar,
                revelar: g.revelar,
                editar: g.editar,
            }));
            submitData.responsavel = submitData.responsavel?.value ?? null;
            // O GET devolve a configuração gravada: o que vale é o estado atual da tela
            ['tiposEnvio', 'webhookMattermost', 'notificacaoMensagem', 'diasAntesExpiracao'].forEach(chave => delete submitData[chave]);
            submitData.notificarResponsavel = notificarResponsavel;
            if (notificarResponsavel) {
                const desconhecidas = variaveisDesconhecidas(notificacaoMensagem);
                if (desconhecidas.length) {
                    alert(`Variável desconhecida na mensagem: {{${desconhecidas[0]}}}. Use: ${VARIAVEIS_MENSAGEM.map(v => `{{${v}}}`).join(', ')}.`);
                    return;
                }
                if (!submitData.dataExpiracao) {
                    alert('Informe a data de expiração para notificar o vencimento.');
                    return;
                }
                submitData.notificarResponsavel = true;
                submitData.tiposEnvio = tiposEnvioSelected.map((t: any) => t.value);
                submitData.notificacaoMensagem = notificacaoMensagem;
                submitData.diasAntesExpiracao = diasAntesExpiracao === '' ? null : Number(diasAntesExpiracao);
                if (hasTipoEnvio(1)) {
                    submitData.webhookMattermost = webhookMattermost?.value ?? null;
                }
                if (hasTipoEnvio(2)) {
                    submitData.clientes = clientes.map((c: any) => c.value || c.id);
                    submitData.marketplaces = marketplace.map((m: any) => m.value);
                    submitData.gatewayPagamento = gateway.map((g: any) => g.value);
                    submitData.formaPagamento = formaPagamento.map((f: any) => f.value);
                    submitData.regimeTributacao = regimeTributacao.map((r: any) => r.value);
                }
            }
            handleSubmitValues(submitData);
        },
        onLoadDadosCompleted: (data: any) => {
            const nomeExibicao = data.cliente?.pessoa?.nome || data.cliente?.nome || '';
            let newData = {
                destinatario: '',
                tipoCredencial: TIPO_SIMPLES,
                tipoNotificacao: '',
                ...data,
                cliente: data.cliente ? {
                    value: data.cliente.id,
                    label: nomeExibicao,
                    pessoa: data.cliente.pessoa
                } : null
            };
            setValues(newData);
            setFirstValueCredencial(newData.credencial);
            setCredencialBase(newData.credencial);
            setFirstTipoCredencial(Number(newData.tipoCredencial));

            const permissoesCarregadas: Permissoes = data.permissoes || { visualizar: true, revelar: true, editar: true };
            setPermissoes(permissoesCarregadas);
            setGrupos(Array.isArray(data.setores) ? data.setores : []);
            setHistorico(Array.isArray(data.historicoNotificacoes) ? data.historicoNotificacoes : []);
            setAlteracoes(Array.isArray(data.historicoAlteracoes) ? data.historicoAlteracoes : []);
            if (action === 'edit' && !permissoesCarregadas.editar) {
                alert('Você não tem permissão para editar esta credencial. Ela será aberta só para visualização.');
                history.replace(`/secrets-manager/visualizar/${data.id}`);
                return;
            }

            // A configuração fica guardada mesmo com a notificação desligada
            setNotificarResponsavel(!!data.notificarResponsavel);
            if (Array.isArray(data.notificacaoTiposEnvio)) {
                setTiposEnvioSelected(
                    data.notificacaoTiposEnvio
                        .map((tipo: number) => tiposEnvioOptions.find(o => o.value === tipo))
                        .filter(Boolean)
                );
            }
            if (data.notificacaoMensagem) setNotificacaoMensagem(data.notificacaoMensagem);
            if (data.diasAntesExpiracao !== null && data.diasAntesExpiracao !== undefined) {
                setDiasAntesExpiracao(String(data.diasAntesExpiracao));
            }
            if (data.webhookMattermost) setWebhookMattermost(data.webhookMattermost);
            if (Array.isArray(data.clientesNotificacao)) setClientes(data.clientesNotificacao);
            setFiltrosSalvos({
                marketplaces: data.marketplaces || [],
                gatewayPagamento: data.gatewayPagamento || [],
                formaPagamento: data.formaPagamento || [],
            });
            if (Array.isArray(data.regimeTributacao)) {
                const regimes = data.regimeTributacao.map(String);
                setRegimeTributacao(listRegimeTributacaoFiltro.filter((o: any) => regimes.includes(String(o.value))));
            }
        }
    });

    useEffect(() => {
        if (!notificarResponsavel) return;
        getApi().get(`/client-status/lists`).then((response: any) => {
            const data = response.data;
            const toList = (obj: any) => Object.keys(obj).map(k => ({ value: k, label: obj[k] }));
            setListGateway(toList(data['gatewayList'] || {}));
            setListMarketplace(toList(data['marketplaceList'] || {}));
            setListFormaPagamento(toList(data['formaPagamentoList'] || {}));
        });
    }, [notificarResponsavel]);

    useEffect(() => {
        if (!filtrosSalvos) return;
        const selecionados = (lista: any[], valores: any[]) =>
            lista.filter(o => (valores || []).map(String).includes(String(o.value)));
        if (listMarketplace.length) setMarketplace(selecionados(listMarketplace, filtrosSalvos.marketplaces));
        if (listGateway.length) setGateway(selecionados(listGateway, filtrosSalvos.gatewayPagamento));
        if (listFormaPagamento.length) setFormaPagamento(selecionados(listFormaPagamento, filtrosSalvos.formaPagamento));
    }, [filtrosSalvos, listMarketplace, listGateway, listFormaPagamento]);

    useEffect(() => {
        getApi().get('/secrets-manager/opcoes').then((response: any) => {
            setOpcoesSetores(response.data?.setores || []);
            setOpcoesUsuarios(response.data?.usuarios || []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!hasTipoEnvio(1)) return;
        getApi().get('/mattermost/webhook/options').then((response: any) => {
            const opts = Array.isArray(response.data) ? response.data : [];
            setListWebhooks(opts);
            if (webhookMattermost?.value) {
                const match = opts.find((o: any) => o.value === webhookMattermost.value);
                if (match) setWebhookMattermost(match);
            }
        }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tiposEnvioSelected]);

    // Helpers
    const isView = () => action === 'view';
    const isNovo = () => !(values.id > 0);
    const isComposta = () => Number(values.tipoCredencial) === TIPO_COMPOSTA;
    const podeEditarCredencial = () => !isView() && (isNovo() || credencialAberta);

    const handleFormReady = (formApi: FormApi) => setFormApi(formApi);

    // --- Mensagem da notificação ---
    const inserirVariavel = (variavel: string) => {
        const textarea = mensagemRef.current;
        const texto = `{{${variavel}}}`;
        const inicio = textarea ? textarea.selectionStart : notificacaoMensagem.length;
        const fim = textarea ? textarea.selectionEnd : notificacaoMensagem.length;
        setNotificacaoMensagem(notificacaoMensagem.slice(0, inicio) + texto + notificacaoMensagem.slice(fim));
        setTimeout(() => {
            if (!textarea) return;
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = inicio + texto.length;
        });
    };

    const previaMensagem = () => {
        const vencida = values.dataExpiracao && new Date(values.dataExpiracao) <= new Date();
        const valores: Record<string, string> = {
            Identificador: values.identificador || '(identificador)',
            DataExpiracao: formatarDataHora(values.dataExpiracao) || 'Sem expiração',
            DiasRestantes: values.dataExpiracao ? String(Math.abs(diasAteVencimento(values.dataExpiracao))) : 'Sem expiração',
            Responsavel: values.responsavel?.label || 'Não informado',
            Cliente: values.cliente?.label || values.cliente?.pessoa?.nome || 'Nenhum',
        };
        const titulo = (vencida ? '⚠️ Credencial vencida: ' : '🔐 Credencial próxima do vencimento: ') + valores.Identificador;
        const corpo = notificacaoMensagem.replace(PADRAO_VARIAVEL, (trecho: string, nome: string) =>
            valores[nome] !== undefined ? valores[nome] : `[variável desconhecida: ${nome}]`);
        return { titulo, corpo };
    };

    // --- Grupos de acesso ---
    const setoresDisponiveis = opcoesSetores.filter(o => !grupos.some(g => String(g.setor.value) === String(o.value)));

    // Escolher o setor no select já adiciona o grupo, só com "visualizar"
    const adicionarGrupo = (setor: Opcao | null) => {
        if (!setor) return;
        setGrupos([...grupos, { setor, visualizar: true, revelar: false, editar: false }]);
    };

    // Revelar e editar exigem visualizar; tirar visualizar tira as outras
    const alterarPermissao = (i: number, permissao: keyof Permissoes, marcado: boolean) => {
        setGrupos(grupos.map((g, idx) => {
            if (idx !== i) return g;
            const novo = { ...g, [permissao]: marcado };
            if (permissao === 'visualizar' && !marcado) {
                novo.revelar = false;
                novo.editar = false;
            }
            if (permissao !== 'visualizar' && marcado) novo.visualizar = true;
            return novo;
        }));
    };

    let searchTimeout: any;

    const promiseOptions = (inputValue: string): Promise<any> => {
        if (!inputValue) return Promise.resolve([]);
        return new Promise((resolve) => {
            if (searchTimeout) clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => {
                getApi()
                    .get(`/cliente/suggest/${inputValue}`)
                    .then((response: any) => {
                        const data = Array.isArray(response.data) ? response.data : [];
                        const options = data.map((item: any) => ({
                            value: item.value,
                            label: item.label || 'Sem Nome'
                        }));
                        resolve(options);
                    })
                    .catch(() => resolve([]));
            }, 600); // 600ms de atraso
        });
    };

    // --- Credencial ---
    const comCredencial = (base: any, value: string, referencia: any = credencialBase) => {
        const newValues = { ...base, credencial: value };
        if (!isNovo()) {
            newValues.newCredencial = referencia !== value;
        }
        return newValues;
    };

    const handleCredencial = (value: string) => setValues(comCredencial(values, value));

    const handleTipoCredencial = (tipo: number) => {
        if (tipo === Number(values.tipoCredencial)) return;
        const base = { ...values, tipoCredencial: tipo };
        setValoresVisiveis({});
        // Voltou ao tipo gravado: restaura a credencial cifrada do banco
        if (!isNovo() && tipo === firstTipoCredencial) {
            delete base.newCredencial;
            setPartes([partePadrao()]);
            setCredencialAberta(false);
            setCredencialBase(firstValueCredencial);
            setValues({ ...base, credencial: firstValueCredencial });
            return;
        }
        setCredencialAberta(true);
        if (tipo === TIPO_COMPOSTA) {
            const novasPartes = [partePadrao()];
            setPartes(novasPartes);
            setValues(comCredencial(base, JSON.stringify(novasPartes)));
        } else {
            setValues(comCredencial(base, ''));
        }
    };

    const substituirCredencial = () => {
        setCredencialAberta(true);
        if (isComposta()) {
            const novasPartes = [partePadrao()];
            setPartes(novasPartes);
            handleCredencial(JSON.stringify(novasPartes));
        } else {
            handleCredencial('');
        }
    };

    // --- Partes ---
    const syncPartes = (novasPartes: Parte[]) => {
        setPartes(novasPartes);
        handleCredencial(JSON.stringify(novasPartes));
    };

    const addParte    = () => syncPartes([...partes, partePadrao()]);
    const removeParte = (i: number) => syncPartes(partes.filter((_, idx) => idx !== i));
    const updateParte = (i: number, field: keyof Parte, value: any) =>
        syncPartes(partes.map((p, idx) => idx === i ? { ...p, [field]: value } : p));

    const alternarVisivel = (i: number) => setValoresVisiveis({ ...valoresVisiveis, [i]: !valoresVisiveis[i] });

    const copiar = (chave: string, texto: string) => {
        navigator.clipboard?.writeText(texto).then(() => {
            setCopiado(chave);
            setTimeout(() => setCopiado(atual => atual === chave ? null : atual), 1500);
        });
    };

    // --- Modal de senha ---
    const abrirModalSenha = (modo: ModoRevelar) => {
        setModoRevelar(modo);
        setErroSenha('');
        setPasswordInput('');
        setShowModal(true);
    };

    // Revelar (ou errar a senha) entra no histórico de alterações: recarrega a aba
    const atualizarAlteracoes = () => {
        getApi().get(`/secrets-manager/get/${values.id}`)
            .then((response: any) => setAlteracoes(response.data?.historicoAlteracoes || []))
            .catch(() => {});
    };

    const confirmReveal = () => {
        if (!passwordInput || loading) return;
        setLoading(true);
        setErroSenha('');
        getApi()
            .post(`/secrets-manager/reveal/${values.id}`, { senha: passwordInput, modo: modoRevelar })
            .then((response: any) => {
                atualizarAlteracoes();
                const decrypted: string = response.data.credencial;
                let parsed: Parte[] = [];
                if (isComposta()) {
                    try {
                        parsed = JSON.parse(decrypted);
                    } catch (e) {
                        parsed = [];
                    }
                    if (!isListaDePartes(parsed)) {
                        setErroSenha('A credencial gravada não está no formato de partes. Use "Substituir credencial" para cadastrá-la de novo.');
                        return;
                    }
                }
                setValoresVisiveis({});
                if (modoRevelar === 'editar') {
                    if (isComposta()) setPartes(parsed);
                    setCredencialBase(decrypted);
                    setCredencialAberta(true);
                    setValues(comCredencial(values, decrypted, decrypted));
                } else if (isComposta()) {
                    setDecryptedPartes(parsed);
                } else {
                    setDecryptedValue(decrypted);
                }
                setShowModal(false);
                setPasswordInput('');
            })
            .catch((error: any) => {
                atualizarAlteracoes();
                const status = error?.response?.status;
                setErroSenha(status === 403
                    ? 'Senha inválida.'
                    : (error?.response?.data?.error || 'Não foi possível descriptografar a credencial.'));
                setPasswordInput('');
            })
            .finally(() => setLoading(false));
    };

    // --- Render: bloco da credencial ainda cifrada ---
    const renderBloqueado = () => (
        <div className="secrets-bloqueado">
            <CIcon name="cil-lock-locked" height={28} />
            <div className="secrets-bloqueado-texto">
                <strong>Conteúdo criptografado</strong>
                <span className="secrets-ajuda">
                    {!permissoes.revelar
                        ? (isView()
                            ? 'Seus grupos de acesso não têm permissão para revelar esta credencial.'
                            : 'Seus grupos de acesso não têm permissão para revelar esta credencial; você pode substituí-la por um novo valor.')
                        : isView()
                            ? 'Confirme sua senha para ver a credencial.'
                            : 'Revele para editar o valor atual ou substitua por um novo. Sem isso, a credencial gravada é mantida.'}
                </span>
            </div>
            <div className="secrets-bloqueado-acoes">
                {isView() ? (
                    permissoes.revelar && (
                        <CButton color="primary" size="sm" onClick={() => abrirModalSenha('ver')}>
                            <CIcon name="cil-lock-locked" className="mr-1" /> Visualizar credencial
                        </CButton>
                    )
                ) : (
                    <>
                        {permissoes.revelar && (
                            <CButton color="primary" size="sm" onClick={() => abrirModalSenha('editar')}>
                                <CIcon name="cil-lock-locked" className="mr-1" />
                                {isComposta() ? 'Carregar partes para editar' : 'Revelar para editar'}
                            </CButton>
                        )}
                        <CButton color="secondary" variant="outline" size="sm" onClick={substituirCredencial}>
                            Substituir credencial
                        </CButton>
                    </>
                )}
            </div>
        </div>
    );

    const botaoCopiar = (chave: string, texto: string) => (
        <CButton color="secondary" variant="ghost" size="sm" title="Copiar" onClick={() => copiar(chave, texto)}>
            <CIcon name={copiado === chave ? 'cil-check' : 'cil-copy'} />
        </CButton>
    );

    const botaoMostrar = (i: number) => (
        <CButton color="secondary" variant="ghost" size="sm" className="secrets-botao-mostrar" onClick={() => alternarVisivel(i)}>
            {valoresVisiveis[i] ? 'Ocultar' : 'Mostrar'}
        </CButton>
    );

    // --- Render: credencial simples ---
    const renderSimples = () => {
        if (isView()) {
            if (!decryptedValue) return renderBloqueado();
            return (
                <div className="secrets-valor-revelado">
                    <CTextarea readOnly rows={4} className="secrets-mono" value={decryptedValue} />
                    <div className="text-right mt-1">
                        <CButton color="secondary" variant="outline" size="sm" onClick={() => copiar('simples', decryptedValue)}>
                            <CIcon name={copiado === 'simples' ? 'cil-check' : 'cil-copy'} className="mr-1" />
                            {copiado === 'simples' ? 'Copiado' : 'Copiar'}
                        </CButton>
                    </div>
                </div>
            );
        }
        if (!podeEditarCredencial()) return renderBloqueado();
        return (
            <CTextarea
                id="credencial"
                name="credencial"
                rows={4}
                className="secrets-mono"
                //@ts-ignore
                onChange={(e) => handleCredencial(e.target.value)}
                value={values.credencial}
                placeholder="Cole ou digite a credencial"
                required
            />
        );
    };

    // --- Render: credencial composta em visualização ---
    const renderCompostaView = () => {
        if (!decryptedPartes) return renderBloqueado();
        return (
            <div className="secrets-partes">
                {decryptedPartes.map((p, i) => {
                    const oculto = Number(p.tipo) === PARTE_SENHA && !valoresVisiveis[i];
                    return (
                        <div key={i} className="secrets-parte">
                            <div className="secrets-parte-campo">
                                {p.campo}
                                <span className="secrets-parte-tipo">{TIPOS_PARTE[p.tipo] ?? p.tipo}</span>
                            </div>
                            <div className="secrets-parte-valor secrets-mono">
                                {Number(p.tipo) === PARTE_URL
                                    ? <a href={p.valor} target="_blank" rel="noopener noreferrer">{p.valor}</a>
                                    : (oculto ? '••••••••••' : p.valor)}
                            </div>
                            <div className="secrets-parte-acoes">
                                {Number(p.tipo) === PARTE_SENHA && botaoMostrar(i)}
                                {botaoCopiar('parte-' + i, p.valor)}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // --- Render: credencial composta em edição ---
    const renderCompostaEdit = () => {
        if (!podeEditarCredencial()) return renderBloqueado();
        return (
            <>
                <div className="secrets-partes">
                    {partes.map((parte, i) => (
                        <div key={i} className="secrets-parte secrets-parte-edicao">
                            <CSelect
                                className="secrets-parte-tipo-select"
                                value={parte.tipo}
                                title="Tipo da parte"
                                onChange={(e: any) => updateParte(i, 'tipo', Number(e.target.value))}
                            >
                                {Object.entries(TIPOS_PARTE).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                ))}
                            </CSelect>
                            <CInput
                                value={parte.campo}
                                placeholder="Nome do campo (ex.: usuário)"
                                onChange={(e: any) => updateParte(i, 'campo', e.target.value)}
                            />
                            <div className="secrets-parte-valor-edicao">
                                <CInput
                                    className="secrets-mono"
                                    type={Number(parte.tipo) === PARTE_SENHA && !valoresVisiveis[i] ? 'password' : 'text'}
                                    value={parte.valor}
                                    placeholder="Valor"
                                    autoComplete="new-password"
                                    onChange={(e: any) => updateParte(i, 'valor', e.target.value)}
                                />
                                {Number(parte.tipo) === PARTE_SENHA && botaoMostrar(i)}
                            </div>
                            <CButton
                                color="danger"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeParte(i)}
                                disabled={partes.length === 1}
                                title="Remover parte"
                            >
                                <CIcon name="cil-trash" />
                            </CButton>
                        </div>
                    ))}
                </div>
                <CButton color="secondary" variant="outline" size="sm" className="mt-2" onClick={addParte}>
                    <CIcon name="cil-plus" className="mr-1" /> Adicionar parte
                </CButton>
            </>
        );
    };

    return (
        <div className="w-100">
            <Form entity="Credencial" handleSubmit={handleSubmit} onFormReady={handleFormReady} className="secrets-form">
                <CRow>
                    <CCol lg={6} className="mb-3">
                        <div className="secrets-secao">
                            <div className="secrets-secao-titulo">Dados da credencial</div>
                            <CFormGroup>
                                <CLabel className={'text-danger'} htmlFor="identificador">Identificador</CLabel>
                                <CInput
                                    readOnly={isView()}
                                    disabled={isView()}
                                    type="text"
                                    id="identificador"
                                    name="identificador"
                                    onChange={handleChange}
                                    value={values.identificador}
                                    placeholder="Ex.: aws-producao-s3"
                                    required
                                />
                                <div className="secrets-ajuda mt-1">Nome usado para recuperar a credencial pela API.</div>
                            </CFormGroup>
                            <CFormGroup>
                                <CLabel htmlFor="cliente">Cliente <small className="text-muted">(opcional)</small></CLabel>
                                {!isView() ? (
                                    <AsyncSelect
                                        inputId="cliente"
                                        defaultOptions
                                        loadOptions={promiseOptions}
                                        onChange={newValue => handleChangeSuggest(newValue, 'cliente')}
                                        value={values.cliente}
                                        placeholder="Pesquisar cliente..."
                                        isClearable
                                    />
                                ) : (
                                    <CInput
                                        readOnly
                                        disabled
                                        value={values?.cliente?.pessoa?.nome || values?.cliente?.label || 'Nenhum cliente selecionado'}
                                    />
                                )}
                            </CFormGroup>
                            <CFormGroup className="mb-0">
                                <CLabel htmlFor="dataExpiracao">Data de expiração <small className="text-muted">(opcional)</small></CLabel>
                                <CInput
                                    readOnly={isView()}
                                    disabled={isView()}
                                    type="datetime-local"
                                    id="dataExpiracao"
                                    name="dataExpiracao"
                                    onChange={handleChange}
                                    value={values.dataExpiracao}
                                    style={{ maxWidth: '16rem' }}
                                />
                            </CFormGroup>
                            <CFormGroup className="mb-0 mt-3">
                                <CLabel htmlFor="responsavel">Responsável pela renovação <small className="text-muted">(opcional)</small></CLabel>
                                {!isView() ? (
                                    <Select
                                        inputId="responsavel"
                                        options={opcoesUsuarios}
                                        value={values.responsavel || null}
                                        onChange={(opt: any) => handleChangeSuggest(opt, 'responsavel')}
                                        placeholder="Selecionar responsável..."
                                        isClearable
                                    />
                                ) : (
                                    <CInput readOnly disabled value={values?.responsavel?.label || 'Não informado'} />
                                )}
                                <div className="secrets-ajuda mt-1">Pessoa encarregada de renovar esta credencial.</div>
                            </CFormGroup>
                        </div>
                    </CCol>

                    <CCol lg={6} className="mb-3">
                        <div className="secrets-secao">
                            <div className="secrets-secao-titulo">Notificação</div>
                            <label className={'secrets-switch' + (isView() ? ' desabilitado' : '')}>
                                <CSwitch
                                    color="primary"
                                    shape="pill"
                                    id="notificarResponsavel"
                                    name="notificarResponsavel"
                                    checked={notificarResponsavel}
                                    onChange={(e: any) => setNotificarResponsavel(e.target.checked)}
                                    disabled={isView()}
                                />
                                Notificar responsável antes de expirar
                            </label>
                            {!notificarResponsavel && (
                                <div className="secrets-ajuda mt-1">Desligado, ninguém é avisado sobre a expiração desta credencial.</div>
                            )}

                            {notificarResponsavel && (
                                <div className="mt-3">
                                    <CFormGroup>
                                        <CLabel className="text-danger">Tipo de envio</CLabel>
                                        <Select
                                            isMulti
                                            options={tiposEnvioOptions}
                                            value={tiposEnvioSelected}
                                            onChange={(opt: any) => setTiposEnvioSelected(opt || [])}
                                            placeholder="Selecione o(s) tipo(s)..."
                                            isDisabled={isView()}
                                            closeMenuOnSelect={false}
                                        />
                                    </CFormGroup>

                                    {hasTipoEnvio(1) && (
                                        <CFormGroup>
                                            <CLabel htmlFor="webhookMattermost">Webhook do Mattermost</CLabel>
                                            {!isView() ? (
                                                <Select
                                                    inputId="webhookMattermost"
                                                    options={listWebhooks}
                                                    value={webhookMattermost}
                                                    onChange={(opt: any) => setWebhookMattermost(opt)}
                                                    placeholder="Selecione um webhook..."
                                                    isDisabled={isView()}
                                                />
                                            ) : (
                                                <CInput
                                                    readOnly
                                                    disabled
                                                    value={webhookMattermost?.label || ''}
                                                />
                                            )}
                                        </CFormGroup>
                                    )}

                                    {hasTipoEnvio(2) && (
                                        <>
                                            <CRow>
                                                <CCol sm={6}>
                                                    <CFormGroup>
                                                        <CLabel className="text-danger" htmlFor="tipoNotificacao">Tipo de notificação</CLabel>
                                                        <CSelect
                                                            id="tipoNotificacao"
                                                            name="tipoNotificacao"
                                                            value={values.tipoNotificacao}
                                                            onChange={handleChange}
                                                            disabled={isView()}
                                                        >
                                                            <option value="">Selecione...</option>
                                                            {NotificacaoTipoList.getList(false).map((item: any) => (
                                                                <option key={item.value} value={item.value}>{item.description}</option>
                                                            ))}
                                                        </CSelect>
                                                    </CFormGroup>
                                                </CCol>
                                                <CCol sm={6}>
                                                    <CFormGroup>
                                                        <CLabel className="text-danger" htmlFor="destinatario">Destinatário</CLabel>
                                                        <CSelect
                                                            id="destinatario"
                                                            name="destinatario"
                                                            value={values.destinatario}
                                                            onChange={handleChange}
                                                            disabled={isView()}
                                                        >
                                                            <option value="">Selecione...</option>
                                                            {NotificacaodestinatarioList.getList(false).map((item: any) => (
                                                                <option key={item.value} value={item.value}>{item.description}</option>
                                                            ))}
                                                        </CSelect>
                                                    </CFormGroup>
                                                </CCol>
                                            </CRow>

                                            {parseInt(values.destinatario) === NotificacaoDestinatario.DESTINATARIO_SELECIONAR_CLIENTE && (
                                                <CFormGroup>
                                                    <CLabel htmlFor="clientesNotif">Cliente(s)</CLabel>
                                                    <AsyncSelect
                                                        id="clientesNotif"
                                                        isMulti
                                                        defaultOptions
                                                        value={clientes}
                                                        loadOptions={promiseOptions}
                                                        onChange={(opt: any) => setClientes(opt || [])}
                                                        placeholder="Pesquisar cliente..."
                                                        isDisabled={isView()}
                                                    />
                                                </CFormGroup>
                                            )}

                                            {parseInt(values.destinatario) === NotificacaoDestinatario.DESTINATARIO_MARKETPLACE && (
                                                <CFormGroup>
                                                    <CLabel htmlFor="marketplaceNotif">Marketplace</CLabel>
                                                    <Select
                                                        id="marketplaceNotif"
                                                        isMulti
                                                        options={listMarketplace}
                                                        value={marketplace}
                                                        onChange={(opt: any) => setMarketplace(opt || [])}
                                                        placeholder="Marketplace..."
                                                        isDisabled={isView()}
                                                        closeMenuOnSelect={false}
                                                    />
                                                </CFormGroup>
                                            )}

                                            {parseInt(values.destinatario) === NotificacaoDestinatario.DESTINATARIO_GATEWAY_PAGAMENTO && (
                                                <>
                                                    <CFormGroup>
                                                        <CLabel htmlFor="gatewayNotif">Gateway de pagamento</CLabel>
                                                        <Select
                                                            id="gatewayNotif"
                                                            isMulti
                                                            options={listGateway}
                                                            value={gateway}
                                                            onChange={(opt: any) => setGateway(opt || [])}
                                                            placeholder="Gateway..."
                                                            isDisabled={isView()}
                                                            closeMenuOnSelect={false}
                                                        />
                                                    </CFormGroup>
                                                    <CFormGroup>
                                                        <CLabel htmlFor="formaPagamentoNotif">Forma de pagamento</CLabel>
                                                        <Select
                                                            id="formaPagamentoNotif"
                                                            isMulti
                                                            options={listFormaPagamento}
                                                            value={formaPagamento}
                                                            onChange={(opt: any) => setFormaPagamento(opt || [])}
                                                            placeholder="Forma de pagamento..."
                                                            isDisabled={isView()}
                                                            closeMenuOnSelect={false}
                                                        />
                                                    </CFormGroup>
                                                </>
                                            )}

                                            {parseInt(values.destinatario) === NotificacaoDestinatario.DESTINATARIO_REGIME_TRIBUTACAO && (
                                                <CFormGroup>
                                                    <CLabel htmlFor="tributacaoNotif">Regime de tributação</CLabel>
                                                    <Select
                                                        id="tributacaoNotif"
                                                        isMulti
                                                        options={listRegimeTributacaoFiltro}
                                                        value={regimeTributacao}
                                                        onChange={(opt: any) => setRegimeTributacao(opt || [])}
                                                        placeholder="Regime de tributação..."
                                                        isDisabled={isView()}
                                                        closeMenuOnSelect={false}
                                                    />
                                                </CFormGroup>
                                            )}
                                        </>
                                    )}

                                    <CFormGroup>
                                        <CLabel htmlFor="notificacaoMensagem" className="text-danger">Mensagem</CLabel>
                                        <CTextarea
                                            id="notificacaoMensagem"
                                            rows={4}
                                            innerRef={mensagemRef}
                                            value={notificacaoMensagem}
                                            //@ts-ignore
                                            onChange={(e: any) => setNotificacaoMensagem(e.target.value)}
                                            placeholder="Ex.: A credencial {{Identificador}} vence em {{DataExpiracao}}."
                                            readOnly={isView()}
                                            disabled={isView()}
                                        />
                                        {!isView() && (
                                            <div className="secrets-variaveis">
                                                {VARIAVEIS_MENSAGEM.map(variavel => (
                                                    <CButton
                                                        key={variavel}
                                                        size="sm"
                                                        color="info"
                                                        variant="outline"
                                                        title="Inserir no texto"
                                                        onClick={() => inserirVariavel(variavel)}
                                                    >
                                                        {`{{${variavel}}}`}
                                                    </CButton>
                                                ))}
                                            </div>
                                        )}
                                        {variaveisDesconhecidas(notificacaoMensagem).length > 0 && (
                                            <div className="invalid-feedback d-block">
                                                Variável desconhecida: {variaveisDesconhecidas(notificacaoMensagem).map(v => `{{${v}}}`).join(', ')}
                                            </div>
                                        )}
                                        {notificacaoMensagem.trim() !== '' && (
                                            <details className="secrets-previa">
                                                <summary>Pré-visualizar a mensagem com as variáveis preenchidas</summary>
                                                <div className="secrets-previa-corpo">
                                                    <strong>{previaMensagem().titulo}</strong>
                                                    {'\n\n'}
                                                    {previaMensagem().corpo}
                                                </div>
                                            </details>
                                        )}
                                    </CFormGroup>

                                    <CFormGroup className="mb-0">
                                        <CLabel htmlFor="diasAntesExpiracao" className="text-danger">Avisar quantos dias antes</CLabel>
                                        <CInput
                                            id="diasAntesExpiracao"
                                            type="number"
                                            min={0}
                                            step={1}
                                            value={diasAntesExpiracao}
                                            onChange={(e: any) => setDiasAntesExpiracao(e.target.value)}
                                            placeholder="Ex.: 7"
                                            readOnly={isView()}
                                            disabled={isView()}
                                            style={{ maxWidth: '8rem' }}
                                        />
                                    </CFormGroup>
                                    <div className="secrets-nota">
                                        Um aviso por dia em cada destino, a partir dessa quantidade de dias antes da expiração.
                                        Se a credencial vencer sem ser renovada, os avisos continuam diariamente até a data de
                                        expiração ser atualizada. Renovar a data começa um novo ciclo.
                                    </div>
                                </div>
                            )}
                        </div>
                    </CCol>
                </CRow>

                <div className="secrets-secao mb-3">
                    <div className="secrets-secao-titulo">
                        <span className="text-danger">Credencial sensível</span>
                        <CButtonGroup size="sm" role="group" aria-label="Tipo de credencial">
                            {[{ tipo: TIPO_SIMPLES, label: 'Simples' }, { tipo: TIPO_COMPOSTA, label: 'Composta' }].map(opcao => {
                                const ativo = Number(values.tipoCredencial) === opcao.tipo;
                                return (
                                    <CButton
                                        key={opcao.tipo}
                                        color="primary"
                                        variant={ativo ? undefined : 'outline'}
                                        disabled={isView() && !ativo}
                                        onClick={() => !isView() && handleTipoCredencial(opcao.tipo)}
                                    >
                                        {opcao.label}
                                    </CButton>
                                );
                            })}
                        </CButtonGroup>
                    </div>
                    <div className="secrets-ajuda mb-2">
                        {isComposta()
                            ? 'Várias partes nomeadas, como usuário, senha e URL de acesso. Cada parte tem um tipo; as do tipo Senha ficam mascaradas.'
                            : 'Um único valor, como um token, uma chave de API ou um certificado.'}
                    </div>

                    {isComposta()
                        ? (isView() ? renderCompostaView() : renderCompostaEdit())
                        : renderSimples()
                    }
                </div>

                <div className="secrets-secao mb-3">
                    <div className="secrets-secao-titulo">
                        <span><CIcon name="cil-people" className="mr-1" /> Grupos de acesso</span>
                    </div>
                    <div className="secrets-ajuda mb-2">
                        Só os membros dos setores vinculados acessam esta credencial, com as permissões marcadas.
                        Os setores e seus membros são cadastrados em Cadastros &gt; Setores. Sem nenhum grupo, só usuários master a veem.
                    </div>
                    <div className="table-responsive">
                        <table className="table table-sm secrets-tabela mb-0">
                            <thead>
                                <tr>
                                    <th>Setor / time</th>
                                    <th className="text-center">Visualizar cadastro</th>
                                    <th className="text-center">Revelar segredo</th>
                                    <th className="text-center">Editar credencial</th>
                                    {!isView() && <th />}
                                </tr>
                            </thead>
                            <tbody>
                                {grupos.length === 0 && (
                                    <tr><td colSpan={5} className="text-center secrets-ajuda py-3">Nenhum grupo vinculado.</td></tr>
                                )}
                                {grupos.map((grupo, i) => (
                                    <tr key={String(grupo.setor.value)}>
                                        <td>{grupo.setor.label}</td>
                                        {(['visualizar', 'revelar', 'editar'] as (keyof Permissoes)[]).map(permissao => (
                                            <td key={permissao} className="text-center">
                                                <input
                                                    type="checkbox"
                                                    aria-label={`${permissao} para ${grupo.setor.label}`}
                                                    checked={grupo[permissao]}
                                                    disabled={isView()}
                                                    onChange={(e) => alterarPermissao(i, permissao, e.target.checked)}
                                                />
                                            </td>
                                        ))}
                                        {!isView() && (
                                            <td className="text-right">
                                                <CButton
                                                    color="danger"
                                                    variant="ghost"
                                                    size="sm"
                                                    title="Remover grupo"
                                                    onClick={() => setGrupos(grupos.filter((_, idx) => idx !== i))}
                                                >
                                                    <CIcon name="cil-trash" />
                                                </CButton>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {!isView() && (
                        <div className="secrets-adicionar-grupo">
                            <div className="secrets-adicionar-grupo-select">
                                <Select
                                    inputId="setorParaAdicionar"
                                    options={setoresDisponiveis}
                                    value={null}
                                    onChange={(opt: any) => adicionarGrupo(opt)}
                                    getOptionLabel={(o: any) => o.membros !== undefined ? `${o.label} (${o.membros} ${o.membros === 1 ? 'membro' : 'membros'})` : o.label}
                                    placeholder="Adicionar setor..."
                                    noOptionsMessage={() => 'Nenhum setor disponível'}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {!isNovo() && (
                    <div className="secrets-secao mb-3">
                        <div className="secrets-abas" role="tablist">
                            <button type="button" role="tab" aria-selected={abaHistorico === 'notificacoes'}
                                    className={abaHistorico === 'notificacoes' ? 'ativa' : ''} onClick={() => setAbaHistorico('notificacoes')}>
                                <CIcon name="cil-bell" className="mr-1" /> Histórico de notificações ({historico.length})
                            </button>
                            <button type="button" role="tab" aria-selected={abaHistorico === 'alteracoes'}
                                    className={abaHistorico === 'alteracoes' ? 'ativa' : ''} onClick={() => setAbaHistorico('alteracoes')}>
                                <CIcon name="cil-people" className="mr-1" /> Histórico de alterações ({alteracoes.length})
                            </button>
                        </div>

                        {abaHistorico === 'notificacoes' && (historico.length === 0 ? (
                            <div className="secrets-ajuda">
                                Nenhum aviso de expiração gerado para esta credencial. Com a notificação ligada, a rotina de
                                notificações gera um aviso por dia a partir de "Avisar quantos dias antes".
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm secrets-tabela mb-0" id="historicoNotificacoes">
                                    <thead>
                                        <tr>
                                            <th>Dia do aviso</th>
                                            <th>Enviado em</th>
                                            <th>Destino</th>
                                            <th>Resultado</th>
                                            <th>Detalhes</th>
                                            <th>Vencimento</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historico.map(envio => (
                                            <tr key={envio.id}>
                                                <td className="text-nowrap">{envio.dia}</td>
                                                <td className="text-nowrap">{envio.enviadoEm || '—'}</td>
                                                <td>{envio.destino}</td>
                                                <td><CBadge color={CORES_RESULTADO[envio.resultado] || 'secondary'}>{envio.resultado}</CBadge></td>
                                                <td>{envio.detalhe}</td>
                                                <td className="text-nowrap">{envio.vencimento}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}

                        {abaHistorico === 'alteracoes' && (alteracoes.length === 0 ? (
                            <div className="secrets-ajuda">Nenhuma alteração registrada para esta credencial.</div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm secrets-tabela mb-0" id="historicoAlteracoes">
                                    <thead>
                                        <tr>
                                            <th>Data / hora</th>
                                            <th>Usuário</th>
                                            <th>Ação</th>
                                            <th>Detalhes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {alteracoes.map(item => (
                                            <tr key={item.id}>
                                                <td className="text-nowrap">{item.dataHora}</td>
                                                <td>{item.usuario}</td>
                                                <td className="text-nowrap"><CBadge color={CORES_ACAO[item.acao] || 'secondary'}>{item.acao}</CBadge></td>
                                                <td className="secrets-detalhe">{item.detalhe}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="secrets-ajuda mt-2">O segredo nunca é gravado no histórico, só o registro de que foi alterado ou revelado.</div>
                            </div>
                        ))}
                    </div>
                )}
            </Form>

            <CModal show={showModal} onClose={() => !loading && setShowModal(false)} color="primary" size="sm">
                <CModalHeader closeButton>
                    <CModalTitle>Confirmação de acesso</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <p className="text-muted mb-3">Confirme sua senha de usuário para descriptografar a credencial.</p>
                    <CFormGroup className="mb-0">
                        <CLabel htmlFor="passwordReveal">Senha</CLabel>
                        <CInput
                            type="password"
                            id="passwordReveal"
                            value={passwordInput}
                            autoFocus
                            invalid={!!erroSenha}
                            onChange={(e: any) => setPasswordInput(e.target.value)}
                            onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); confirmReveal(); } }}
                            autoComplete="current-password"
                        />
                        {erroSenha && <div className="invalid-feedback d-block">{erroSenha}</div>}
                    </CFormGroup>
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" onClick={() => setShowModal(false)} disabled={loading}>
                        Cancelar
                    </CButton>
                    <CButton color="primary" onClick={confirmReveal} disabled={loading || !passwordInput}>
                        {loading ? (
                            <>
                                <CSpinner size="sm" aria-hidden="true" className="mr-2"/>
                                Verificando...
                            </>
                        ) : (
                            'Confirmar'
                        )}
                    </CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default SecretsManagerForm;
